// =============================================================================
// PEDRA — src/lib/projectFile.ts
// Exporta e importa o `Project` inteiro em JSON (`.pedra.json`).
//
// A leitura NAO confia em nada: todo campo passa por coercao defensiva, o que
// falta recebe o padrao, o que esta errado e descartado e as listas grandes
// demais sao cortadas. Um arquivo adulterado nunca derruba o app.
// =============================================================================

import type {
  CutOrientation,
  DimensionOrder,
  EdgeBanding,
  EdgeBandType,
  GrainOrientation,
  Id,
  OptimizationPriority,
  Part,
  Project,
  Settings,
  StackMode,
  StockSheet,
  Unit,
} from '../core/types';
import { PROJECT_VERSION } from '../core/types';
import {
  colorForIndex,
  createDefaultSettings,
  emptyEdgeBanding,
  newId,
  syncIdCounterFromProject,
} from '../core/defaults';
import { isValidHexColor, LIMITS } from '../core/validation';
import { downloadText } from './csv';
import { slugifyFilename } from './format';

// -----------------------------------------------------------------------------
// CONSTANTES DO FORMATO
// -----------------------------------------------------------------------------

/** Marca gravada no arquivo para reconhecer que ele e nosso. */
export const PROJECT_FILE_FORMAT = 'pedra-project';

/** Extensao usada no download. */
export const PROJECT_FILE_EXTENSION = '.pedra.json';

/** Identificacao do gerador, so informativa. */
export const PROJECT_FILE_GENERATOR = 'Pedra 1.0';

/** MIME do arquivo de projeto. */
export const PROJECT_FILE_MIME = 'application/json;charset=utf-8';

const UNITS: readonly Unit[] = ['mm', 'cm', 'm', 'in', 'ft'];
const DIMENSION_ORDERS: readonly DimensionOrder[] = ['lengthFirst', 'widthFirst'];
const PRIORITIES: readonly OptimizationPriority[] = ['leastWaste', 'leastCuts', 'smallerSheets'];
const CUT_ORIENTATIONS: readonly CutOrientation[] = ['optimal', 'preferWidth', 'preferLength'];
const STACK_MODES: readonly StackMode[] = ['auto', 'never', 'always'];

// -----------------------------------------------------------------------------
// COERCAO DEFENSIVA
// -----------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Numero finito >= 0, arredondado, com teto. Fora disso devolve `fallback`. */
function coerceDimension(v: unknown, fallback = 0): number {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return fallback;
  return Math.min(Math.round(v), LIMITS.MAX_DIMENSION);
}

function coerceInt(v: unknown, fallback: number, min: number, max: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  return Math.max(min, Math.min(max, Math.round(v)));
}

function coerceBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

function coerceString(v: unknown, fallback: string, maxLength: number): string {
  if (typeof v !== 'string') return fallback;
  return v.slice(0, maxLength);
}

function coerceOneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

function coerceGrain(v: unknown): GrainOrientation {
  return v === 1 || v === 2 ? v : 0;
}

/** Id valido e nao repetido; caso contrario gera um novo. */
function coerceId(v: unknown, prefix: string, seen: Set<Id>): Id {
  const id = typeof v === 'string' && v.trim() !== '' ? v.trim().slice(0, 64) : '';
  if (id === '' || seen.has(id)) {
    let fresh = newId(prefix);
    while (seen.has(fresh)) fresh = newId(prefix);
    seen.add(fresh);
    return fresh;
  }
  seen.add(id);
  return id;
}

function coerceEdgeBanding(v: unknown, validBandIds: ReadonlySet<Id>): EdgeBanding {
  const out = emptyEdgeBanding();
  if (!isRecord(v)) return out;
  for (const side of ['top', 'right', 'bottom', 'left'] as const) {
    const raw = v[side];
    if (typeof raw === 'string' && validBandIds.has(raw)) out[side] = raw;
  }
  return out;
}

/** Configuracoes: tudo que nao for reconhecido volta ao padrao. */
export function sanitizeSettings(v: unknown): Settings {
  const base = createDefaultSettings();
  if (!isRecord(v)) return base;
  return {
    kerf: Math.min(coerceDimension(v.kerf, base.kerf), LIMITS.MAX_KERF),
    considerGrain: coerceBool(v.considerGrain, base.considerGrain),
    considerMaterial: coerceBool(v.considerMaterial, base.considerMaterial),
    edgeBandingEnabled: coerceBool(v.edgeBandingEnabled, base.edgeBandingEnabled),
    labelsOnPanels: coerceBool(v.labelsOnPanels, base.labelsOnPanels),
    useSingleStockUnit: coerceBool(v.useSingleStockUnit, base.useSingleStockUnit),
    unit: coerceOneOf(v.unit, UNITS, base.unit),
    dimensionOrder: coerceOneOf(v.dimensionOrder, DIMENSION_ORDERS, base.dimensionOrder),
    decimalPlaces: coerceInt(v.decimalPlaces, base.decimalPlaces, 0, 3),
    minTrimDimension: coerceDimension(v.minTrimDimension, base.minTrimDimension),
    stackSamePanels: coerceOneOf(v.stackSamePanels, STACK_MODES, base.stackSamePanels),
    priority: coerceOneOf(v.priority, PRIORITIES, base.priority),
    cutOrientation: coerceOneOf(v.cutOrientation, CUT_ORIENTATIONS, base.cutOrientation),
    timeBudgetMs: coerceInt(v.timeBudgetMs, base.timeBudgetMs, 100, 600_000),
    seed: coerceInt(v.seed, base.seed, 0, Number.MAX_SAFE_INTEGER),
  };
}

function sanitizeEdgeBands(v: unknown, seen: Set<Id>): EdgeBandType[] {
  if (!Array.isArray(v)) return [];
  const out: EdgeBandType[] = [];
  for (const raw of v.slice(0, LIMITS.MAX_ROWS)) {
    if (!isRecord(raw)) continue;
    const color = coerceString(raw.color, '', 9);
    out.push({
      id: coerceId(raw.id, 'eb', seen),
      name: coerceString(raw.name, '', LIMITS.MAX_NAME_LENGTH),
      thickness: coerceDimension(raw.thickness, 1_000),
      color: isValidHexColor(color) ? color : '#8C8C8C',
    });
  }
  return out;
}

function sanitizeParts(v: unknown, seen: Set<Id>, bandIds: ReadonlySet<Id>): Part[] {
  if (!Array.isArray(v)) return [];
  const out: Part[] = [];
  for (const raw of v.slice(0, LIMITS.MAX_ROWS)) {
    if (!isRecord(raw)) continue;
    const color = coerceString(raw.color, '', 9);
    out.push({
      id: coerceId(raw.id, 'p', seen),
      length: coerceDimension(raw.length, 0),
      width: coerceDimension(raw.width, 0),
      qty: coerceInt(raw.qty, 1, 0, LIMITS.MAX_QTY),
      label: coerceString(raw.label, '', LIMITS.MAX_LABEL_LENGTH),
      material: coerceString(raw.material, '', LIMITS.MAX_NAME_LENGTH),
      orientation: coerceGrain(raw.orientation),
      enabled: coerceBool(raw.enabled, true),
      edgeBanding: coerceEdgeBanding(raw.edgeBanding, bandIds),
      color: isValidHexColor(color) ? color : colorForIndex(out.length),
    });
  }
  return out;
}

function sanitizeStock(v: unknown, seen: Set<Id>): StockSheet[] {
  if (!Array.isArray(v)) return [];
  const out: StockSheet[] = [];
  for (const raw of v.slice(0, LIMITS.MAX_ROWS)) {
    if (!isRecord(raw)) continue;
    const qty = raw.qty;
    out.push({
      id: coerceId(raw.id, 's', seen),
      length: coerceDimension(raw.length, 0),
      width: coerceDimension(raw.width, 0),
      qty: qty === null || qty === undefined ? null : coerceInt(qty, 1, 0, LIMITS.MAX_QTY),
      label: coerceString(raw.label, '', LIMITS.MAX_LABEL_LENGTH),
      material: coerceString(raw.material, '', LIMITS.MAX_NAME_LENGTH),
      orientation: coerceGrain(raw.orientation),
      enabled: coerceBool(raw.enabled, true),
    });
  }
  return out;
}

/**
 * Transforma um objeto qualquer (JSON, link compartilhado, localStorage) num
 * `Project` valido. Devolve `null` apenas quando o objeto nao tem cara de
 * projeto ou quando a versao e mais nova que a suportada.
 */
export function sanitizeProject(raw: unknown): Project | null {
  if (!isRecord(raw)) return null;

  const version = raw.version;
  if (typeof version === 'number' && Number.isFinite(version) && version > PROJECT_VERSION) {
    return null;
  }
  if (!Array.isArray(raw.parts) && !Array.isArray(raw.stock)) return null;

  const seen = new Set<Id>();
  const edgeBands = sanitizeEdgeBands(raw.edgeBands, seen);
  const bandIds = new Set<Id>(edgeBands.map((b) => b.id));

  const name = coerceString(raw.name, '', LIMITS.MAX_NAME_LENGTH).trim();

  return {
    version: PROJECT_VERSION,
    name: name === '' ? 'Projeto importado' : name,
    parts: sanitizeParts(raw.parts, seen, bandIds),
    stock: sanitizeStock(raw.stock, seen),
    settings: sanitizeSettings(raw.settings),
    edgeBands,
  };
}

// -----------------------------------------------------------------------------
// EXPORTACAO
// -----------------------------------------------------------------------------

/**
 * Serializa o projeto em JSON identado, com as chaves em ordem estavel para
 * que dois exports do mesmo projeto sejam identicos byte a byte.
 */
export function exportProjectJson(p: Project): string {
  const settings = p.settings;
  const payload = {
    _format: PROJECT_FILE_FORMAT,
    _generator: PROJECT_FILE_GENERATOR,
    version: PROJECT_VERSION,
    name: p.name,
    settings: {
      unit: settings.unit,
      dimensionOrder: settings.dimensionOrder,
      decimalPlaces: settings.decimalPlaces,
      kerf: settings.kerf,
      minTrimDimension: settings.minTrimDimension,
      considerGrain: settings.considerGrain,
      considerMaterial: settings.considerMaterial,
      edgeBandingEnabled: settings.edgeBandingEnabled,
      labelsOnPanels: settings.labelsOnPanels,
      useSingleStockUnit: settings.useSingleStockUnit,
      stackSamePanels: settings.stackSamePanels,
      priority: settings.priority,
      cutOrientation: settings.cutOrientation,
      timeBudgetMs: settings.timeBudgetMs,
      seed: settings.seed,
    },
    edgeBands: p.edgeBands.map((b) => ({
      id: b.id,
      name: b.name,
      thickness: b.thickness,
      color: b.color,
    })),
    parts: p.parts.map((part) => ({
      id: part.id,
      length: part.length,
      width: part.width,
      qty: part.qty,
      label: part.label,
      material: part.material,
      orientation: part.orientation,
      enabled: part.enabled,
      color: part.color,
      edgeBanding: {
        top: part.edgeBanding.top,
        right: part.edgeBanding.right,
        bottom: part.edgeBanding.bottom,
        left: part.edgeBanding.left,
      },
    })),
    stock: p.stock.map((s) => ({
      id: s.id,
      length: s.length,
      width: s.width,
      qty: s.qty,
      label: s.label,
      material: s.material,
      orientation: s.orientation,
      enabled: s.enabled,
    })),
  };
  return JSON.stringify(payload, null, 2);
}

/** Nome de arquivo sugerido: `armario-de-cozinha.pedra.json`. */
export function projectFileName(p: Project): string {
  return `${slugifyFilename(p.name)}${PROJECT_FILE_EXTENSION}`;
}

/** Dispara o download do projeto em JSON. */
export function downloadProject(p: Project): void {
  downloadText(projectFileName(p), exportProjectJson(p), PROJECT_FILE_MIME);
}

// -----------------------------------------------------------------------------
// IMPORTACAO
// -----------------------------------------------------------------------------

/**
 * Le o texto de um arquivo `.pedra.json`. Nunca lanca: devolve
 * `{ project, error: null }` no sucesso e `{ project: null, error }` com a
 * mensagem pronta em pt-BR no fracasso.
 */
export function importProjectJson(text: string): { project: Project | null; error: string | null } {
  if (typeof text !== 'string' || text.trim() === '') {
    return { project: null, error: 'O arquivo está vazio.' };
  }

  let raw: unknown;
  try {
    raw = JSON.parse(text.replace(/^\uFEFF/, ''));
  } catch {
    return {
      project: null,
      error: 'O arquivo não é um JSON válido. Verifique se ele não foi editado ou cortado.',
    };
  }

  if (!isRecord(raw)) {
    return { project: null, error: 'O arquivo não parece um projeto do Pedra.' };
  }

  const version = raw.version;
  if (typeof version === 'number' && Number.isFinite(version) && version > PROJECT_VERSION) {
    return {
      project: null,
      error: `Este projeto foi criado numa versão mais nova do Pedra (formato ${version}). Atualize a página e tente de novo.`,
    };
  }

  const project = sanitizeProject(raw);
  if (project === null) {
    return {
      project: null,
      error: 'O arquivo não parece um projeto do Pedra: não encontramos a lista de peças nem a de chapas.',
    };
  }

  // Depois de trazer ids de fora, o contador precisa passar de todos eles.
  syncIdCounterFromProject(project);
  return { project, error: null };
}

/**
 * Le um `File` escolhido no `<input type="file">` como texto UTF-8.
 * Rejeita com `Error` de mensagem em pt-BR.
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (typeof FileReader === 'undefined') {
      reject(new Error('Este navegador não consegue ler arquivos locais.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(typeof result === 'string' ? result : '');
    };
    reader.onerror = () => {
      reject(new Error(`Não foi possível ler o arquivo "${file.name}".`));
    };
    reader.onabort = () => {
      reject(new Error('A leitura do arquivo foi cancelada.'));
    };
    reader.readAsText(file, 'UTF-8');
  });
}
